import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { formatDuration, formatPercent } from './formatters'
import { slaTier, slaRateTier, tierText, tierBorder } from './slaHealth'
import { cn } from '@/lib/utils'
import type { FirstResponseTimePanel } from '@/types/analytics'

interface FirstResponseTimeCardProps {
  data: FirstResponseTimePanel
  mode: 'absolute' | 'relative'
}

const TIER_FILL: Record<'good' | 'warn' | 'bad', string> = {
  good: '#10b981',
  warn: '#f59e0b',
  bad: '#f43f5e',
}

export function FirstResponseTimeCard({ data, mode }: FirstResponseTimeCardProps) {
  const stats = mode === 'relative' ? data.relative : data.absolute
  const slaRateT = slaRateTier(stats.sla_pct)
  const p50T = slaTier(stats.p50, data.sla_seconds)
  const p90T = slaTier(stats.p90, data.sla_seconds)
  const p99T = slaTier(stats.p99, data.sla_seconds)

  return (
    <Card className="p-4">
      <CardHeader className="px-0 pb-3 flex-row items-start justify-between space-y-0 gap-3">
        <div>
          <CardTitle className="text-base">First-response time</CardTitle>
          <CardDescription>
            {mode === 'relative' ? 'Within support hours' : 'Wall-clock'} · {data.total_replies.toLocaleString()} replies
          </CardDescription>
        </div>
        <Badge variant="outline" className={cn('text-xs', tierBorder[slaRateT], tierText[slaRateT])}>
          SLA {formatPercent(stats.sla_pct, 0)} &lt; {formatDuration(data.sla_seconds)}
        </Badge>
      </CardHeader>
      <CardContent className="px-0 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={stats.buckets} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="currentColor" className="text-muted-foreground" />
            <YAxis tick={{ fontSize: 11 }} stroke="currentColor" className="text-muted-foreground" allowDecimals={false} />
            <Tooltip contentStyle={{ background: 'var(--background)', border: '1px solid var(--border)' }} />
            <ReferenceLine y={0} stroke="currentColor" />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {stats.buckets.map((bucket) => (
                <Cell key={bucket.key} fill={TIER_FILL[bucket.tier]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
      <div className="flex justify-around pt-2 text-xs text-muted-foreground">
        <span>P50 · <span className={cn('font-medium', tierText[p50T])}>{formatDuration(stats.p50)}</span></span>
        <span>P90 · <span className={cn('font-medium', tierText[p90T])}>{formatDuration(stats.p90)}</span></span>
        <span>P99 · <span className={cn('font-medium', tierText[p99T])}>{formatDuration(stats.p99)}</span></span>
      </div>
    </Card>
  )
}
